/**
 * Dashboard Backtests Route
 * List and manage backtests
 */

import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useAppStore } from '@/lib/store'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/trading/card'
import { StatusBadge } from '@/components/trading/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/trading/input'
import { Select } from '@/components/trading/input'
import { Plus, TrendingUp, Calendar, DollarSign, BarChart3 } from 'lucide-react'
import { formatNumber, formatPercent } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/backtests')({
  component: BacktestsList,
})

function BacktestsList() {
  const { state, actions } = useAppStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'running' | 'completed' | 'failed'>('all')

  // Mock backtests data
  const mockBacktests = [
    {
      id: '1',
      name: 'Trend Following - 2024 Analysis',
      algorithmId: '1',
      algorithmName: 'Trend Following EUR/USD',
      date: '2024-03-15',
      status: 'completed' as const,
      configuration: {
        symbol: 'EUR/USD',
        startDate: '2024-01-01',
        endDate: '2024-03-15',
        initialCapital: 10000,
      },
      results: {
        finalCapital: 11250,
        totalReturn: 1250,
        totalReturnPercent: 12.5,
        winRate: 65,
        profitFactor: 2.1,
        maxDrawdown: 8.5,
        sharpeRatio: 1.8,
        totalTrades: 45,
      },
    },
    {
      id: '2',
      name: 'Mean Reversion - Stress Test',
      algorithmId: '2',
      algorithmName: 'Mean Reversion GBP/USD',
      date: '2024-03-14',
      status: 'completed' as const,
      configuration: {
        symbol: 'GBP/USD',
        startDate: '2023-06-01',
        endDate: '2024-03-14',
        initialCapital: 10000,
      },
      results: {
        finalCapital: 10450,
        totalReturn: 450,
        totalReturnPercent: 4.5,
        winRate: 58,
        profitFactor: 1.6,
        maxDrawdown: 12.3,
        sharpeRatio: 1.2,
        totalTrades: 28,
      },
    },
    {
      id: '3',
      name: 'BTC Momentum - Current Test',
      algorithmId: '3',
      algorithmName: 'Momentum BTC/USD',
      date: '2024-03-15',
      status: 'running' as const,
      configuration: {
        symbol: 'BTC/USD',
        startDate: '2024-01-01',
        endDate: '2024-03-15',
        initialCapital: 10000,
      },
    },
  ]

  const backtests = state.backtests.length > 0 ? state.backtests : mockBacktests

  // Filter backtests
  const filteredBacktests = backtests.filter((bt) => {
    const matchesSearch = bt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bt.algorithmName?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = filterStatus === 'all' || bt.status === filterStatus
    return matchesSearch && matchesStatus
  })

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Backtests</h1>
          <p className="text-muted-foreground mt-1">
            Test your strategies with historical data
          </p>
        </div>
        <Link to="/dashboard/backtests/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Run Backtest
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search backtests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
                size="sm"
              >
                All ({backtests.length})
              </Button>
              <Button
                variant={filterStatus === 'running' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('running')}
                size="sm"
              >
                Running ({backtests.filter((b) => b.status === 'running').length})
              </Button>
              <Button
                variant={filterStatus === 'completed' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('completed')}
                size="sm"
              >
                Completed ({backtests.filter((b) => b.status === 'completed').length})
              </Button>
              <Button
                variant={filterStatus === 'failed' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('failed')}
                size="sm"
              >
                Failed ({backtests.filter((b) => b.status === 'failed').length})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backtests Grid */}
      {filteredBacktests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">No backtests found</p>
            <p className="text-sm mb-4">
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Run your first backtest to analyze your strategy'}
            </p>
            {!searchQuery && filterStatus === 'all' && (
              <Link to="/dashboard/backtests/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Run Backtest
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredBacktests.map((backtest) => (
            <Link key={backtest.id} to={`/dashboard/backtests/${backtest.id}`}>
              <Card className="hover:border-primary/50 cursor-pointer h-full">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{backtest.name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{backtest.algorithmName}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">{backtest.configuration.symbol}</span>
                        <StatusBadge status={backtest.status} />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {backtest.status === 'running' ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                        Running backtest...
                      </div>
                    </div>
                  ) : backtest.results ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Return: </span>
                        <span className={`font-semibold ${backtest.results.totalReturn >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {backtest.results.totalReturn >= 0 ? '+' : ''}{formatPercent(backtest.results.totalReturnPercent)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Win Rate: </span>
                        <span className="font-semibold">{formatPercent(backtest.results.winRate)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Profit Factor: </span>
                        <span className="font-semibold">{backtest.results.profitFactor.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sharpe: </span>
                        <span className="font-semibold">{backtest.results.sharpeRatio.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Max DD: </span>
                        <span className="font-semibold text-destructive">{formatPercent(backtest.results.maxDrawdown)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Trades: </span>
                        <span className="font-semibold">{backtest.results.totalTrades}</span>
                      </div>
                    </div>
                  ) : null}

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(backtest.date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      Initial: ${formatNumber(backtest.configuration.initialCapital)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
